import * as PIXI from "pixi.js";
import FXManager from "../Engine/Effects/FXManager";

/**
 * The coin shower in a popup spine's own `coin_shower` slot — the way the win
 * celebration runs it (WinAnimationSecondaryController._coinShowerFor), for
 * the other popups that show it: the FS Total Win (win_celebration,
 * `total_win` skin), the H&W Total Win (feature_outro) and the Royal
 * (zero_win). 2026-10-02.
 *
 * The slot (and its bone) is hand-added, FIRST in the draw order (behind every
 * piece of the popup), keyed in `in` and `out` with the popup's own main-bone
 * scale — the coins grow in with it and are pulled away with it — and `out`
 * fades the slot to 0 over its first two thirds.
 *
 * The shower sits in a HOLDER: spine-pixi rewrites a slot object's own
 * transform from its bone every frame, so a scale set on it never shows. The
 * holder takes the bone; the shower inside keeps its scale.
 */
export const COIN_SHOWER_SLOT = "coin_shower";
// The size approved on the win celebration (Vladimir, 2026-10-01: "spot on").
// Spine units — the spine's own scale is the parent's.
export const COIN_SHOWER_SCALE = 2;

/** Does this spine carry the hand-added slot? */
export const hasCoinShowerSlot = (spine) =>
  !!spine?.skeleton?.findSlot(COIN_SHOWER_SLOT);

/**
 * Start the shower in `spine`'s `coin_shower` slot.
 * @returns {{ shower: PIXI.Container, holder: PIXI.Container } | null}
 *          null when the spine has no slot or FX is not ready.
 */
export const startCoinShowerInSlot = (spine, density = 1, name = "coin_shower") => {
  if (!hasCoinShowerSlot(spine) || !FXManager.isReady) return null;
  const skeleton = spine.skeleton;
  // From full size and fully opaque — a previous `out` left the bone at 0
  // and the slot faded out.
  skeleton.findBone(COIN_SHOWER_SLOT)?.setToSetupPose();
  skeleton.findSlot(COIN_SHOWER_SLOT)?.setToSetupPose();

  const shower = new PIXI.Container();
  shower.name = name;
  shower.eventMode = "none";
  shower.scale.set(COIN_SHOWER_SCALE);

  const holder = new PIXI.Container();
  holder.name = `${name}_slot`;
  holder.eventMode = "none";
  holder.addChild(shower);
  spine.addSlotObject(COIN_SHOWER_SLOT, holder);

  FXManager.startCoinShower(shower, density);
  return { shower, holder };
};

/** Stop it and take it out of the slot. Safe to call twice. */
export const stopCoinShowerInSlot = (spine, handle) => {
  FXManager.stopCoinShower();
  if (!handle) return;
  const { shower, holder } = handle;
  if (holder && spine?.getSlotObject?.(COIN_SHOWER_SLOT) === holder) {
    spine.removeSlotObject(COIN_SHOWER_SLOT);
  }
  if (holder && !holder.destroyed) holder.destroy();
  if (shower?.parent) shower.parent.removeChild(shower);
  if (shower && !shower.destroyed) shower.destroy({ children: true });
};
